export interface BrandTokens {
  primary: string;
  primaryDark: string;
  ink: string;
  accent: string;
  surface: string;
  fontBody: string;
  fontHeading: string;
  logoPath: string;
  motif: "arrow" | "shield" | "none";
}

function hexToRgbTriplet(hex: string): string {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

/** Apply agency brand tokens as CSS variables on :root. Tailwind colors read the *-rgb triplets. */
export function applyBrand(b: BrandTokens) {
  const root = document.documentElement.style;
  const set = (name: string, hex: string) => {
    root.setProperty(`--brand-${name}`, hex);
    root.setProperty(`--brand-${name}-rgb`, hexToRgbTriplet(hex));
  };
  set("primary", b.primary);
  set("primary-dark", b.primaryDark);
  set("ink", b.ink);
  set("accent", b.accent);
  set("surface", b.surface);
  root.setProperty("--brand-font-body", `"${b.fontBody}"`);
  root.setProperty("--brand-font-heading", `"${b.fontHeading}"`);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", b.ink);
}

export function money(cents: number | null | undefined, opts: { showSign?: boolean } = {}): string {
  if (cents == null) return "—";
  const v = cents / 100;
  const s = v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  return opts.showSign && v > 0 ? `+${s}` : s;
}

export function shortDate(iso: string | null | undefined, locale: "en" | "es" = "en"): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale === "es" ? "es-MX" : "en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function relativeDays(days: number, locale: "en" | "es"): string {
  if (locale === "es") {
    if (days < 0) return `hace ${-days} d`;
    if (days === 0) return "hoy";
    if (days === 1) return "mañana";
    return `en ${days} d`;
  }
  if (days < 0) return `${-days}d ago`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days}d`;
}

/** +19155551234 → (915) 555-1234; anything else passes through. */
export function formatPhone(p: string | null | undefined): string | undefined {
  if (!p) return undefined;
  const d = p.replace(/\D/g, "").replace(/^1(\d{10})$/, "$1");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p;
}

/** Read a File as base64 (no data-URL prefix). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error ?? new Error("read failed"));
    r.onload = () => {
      const s = String(r.result ?? "");
      resolve(s.includes(",") ? s.slice(s.indexOf(",") + 1) : s);
    };
    r.readAsDataURL(file);
  });
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function pct(v: number | null | undefined, digits = 0): string {
  if (v == null || isNaN(v)) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(digits)}%`;
}
